import React, { useState } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Download, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { trpcClient } from "@/lib/trpc";
import { formatMoney, formatDate } from "@/lib/utils";
import { DatePickerWithRange } from "@/components/ui/date-range-picker";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { Link } from "@tanstack/react-router";

import { Checkbox } from "@/components/ui/checkbox";
import PrintDialog from "@/components/shared/PrintDialog";
import VendorDebtReconciliationPrintTemplate from "@/components/finance/print/VendorDebtReconciliationPrintTemplate";
import { EntityLink, EntityType } from "@/components/shared/EntityLink";
import { toVnDateString } from "@apv-erp/status";

export default function APLedgerView() {
  const { id: vendorId } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState<any>();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isPrintOpen, setIsPrintOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["ap-ledger", vendorId, dateRange],
    queryFn: () =>
      trpcClient.ap.getLedger.query({
        vendorId,
        fromDate: dateRange?.from ? toVnDateString(dateRange.from) : undefined,
        toDate: dateRange?.to ? toVnDateString(dateRange.to) : undefined,
      }),
    enabled: !!vendorId,
  });

  const ledgerData = data?.ledger || [];
  const vendor = data?.vendor;
  const openingBalance = data?.openingBalance || 0;
  const closingBalance = data?.closingBalance || 0;

  const resolveEntityType = (refId: string): EntityType | null => {
    if (!refId) return null;
    if (refId.startsWith("PO-") || refId.startsWith("PR-")) return "purchaseOrder";
    if (refId.startsWith("OS-")) return "outsourceOrder";
    if (refId.startsWith("WH-") || refId.startsWith("REC-") || refId.startsWith("ISS-")) return "warehouseDocument";
    return null;
  };

  return (
    <StandardDetailLayout
      title="Sổ chi tiết công nợ"
      // Ghi chú / đính kèm / lịch sử của sổ công nợ đối tác; thời gian = phát sinh đầu tiên / mới nhất trong kỳ
      entityType="ap_ledger"
      entityId={vendorId}
      timestamps={{
        // chưa có phát sinh: sổ mở cùng hồ sơ đối tác
        created: ledgerData.length ? ledgerData[0]?.date : vendor?.createdAt,
        updated: ledgerData.length ? ledgerData[ledgerData.length - 1]?.date : vendor?.updatedAt || vendor?.createdAt,
        createdBy: ledgerData[0]?.refId ? `chứng từ ${ledgerData[0].refId}` : "Chưa có phát sinh",
        updatedBy: ledgerData[ledgerData.length - 1]?.refId ? `chứng từ ${ledgerData[ledgerData.length - 1].refId}` : "Chưa có phát sinh",
      }}
      breadcrumbs={[
        { label: "Công nợ phải trả", to: "/finance/ap" },
        { label: vendor?.code || "Chi tiết" },
      ]}
      actions={
        <div className="flex items-center gap-2">
          <DatePickerWithRange value={dateRange} onChange={setDateRange} />
          <Button variant="outline" onClick={() => setIsPrintOpen(true)} disabled={selectedIds.length === 0}>
            <Printer className="w-4 h-4 mr-2" />
            In Đối Soát ({selectedIds.length})
          </Button>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Xuất Excel
          </Button>
        </div>
      }
    >
      <div className="w-full px-4 sm:px-6 py-4 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">
                Dư nợ đầu kỳ
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className={`text-2xl font-bold ${openingBalance > 0 ? "text-rose-600" : "text-emerald-600"}`}
              >
                {formatMoney(openingBalance)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">
                Phát sinh trong kỳ
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Phát sinh nợ (Mua hàng / Gia công):</span>
                  <span className="font-semibold text-rose-600">
                    {formatMoney(
                      ledgerData.reduce(
                        (sum: number, item: any) => sum + item.debit,
                        0,
                      ),
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Phát sinh có (Đã thanh toán):</span>
                  <span className="font-semibold text-emerald-600">
                    {formatMoney(
                      ledgerData.reduce(
                        (sum: number, item: any) => sum + item.credit,
                        0,
                      ),
                    )}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">
                Dư nợ cuối kỳ
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className={`text-2xl font-bold ${closingBalance > 0 ? "text-rose-600" : "text-emerald-600"}`}
              >
                {formatMoney(closingBalance)}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <div className="overflow-x-auto min-h-[40vh]">
            <table className="w-full text-sm caption-bottom">
              <thead>
                <tr className="border-b bg-slate-50/50">
                  <th className="px-4 py-3 text-center w-[40px]">
                    <Checkbox
                      checked={ledgerData && ledgerData.length > 0 && selectedIds.length === ledgerData.length}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedIds(ledgerData.map((d: any) => d.id));
                        } else {
                          setSelectedIds([]);
                        }
                      }}
                    />
                  </th>
                  <th className="px-4 py-3 text-center font-medium text-slate-500 w-[100px]">
                    Ngày
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-slate-500 w-[150px]">
                    Số chứng từ
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-slate-500 min-w-[200px]">
                    Diễn giải
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-slate-500 w-[150px]">
                    Ghi nợ
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-slate-500 w-[150px]">
                    Ghi có
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-slate-500 w-[150px]">
                    Số dư
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoading ? (
                  Array(5)
                    .fill(0)
                    .map((_, i) => (
                      <tr key={i}>
                        <td colSpan={6} className="p-4">
                          <div className="h-6 bg-slate-100 rounded animate-pulse" />
                        </td>
                      </tr>
                    ))
                ) : ledgerData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center py-12 text-slate-400"
                    >
                      Không có phát sinh trong kỳ
                    </td>
                  </tr>
                ) : (
                  <>
                    <tr className="bg-slate-50/30">
                      <td className="px-4 py-3 text-center"></td>
                      <td className="px-4 py-3 text-center font-medium text-slate-500">
                        -
                      </td>
                      <td className="px-4 py-3 text-left font-medium text-slate-500">
                        -
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-700">
                        Dư đầu kỳ
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-slate-400">
                        -
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-slate-400">
                        -
                      </td>
                      <td
                        className={`px-4 py-3 text-right font-bold ${openingBalance > 0 ? "text-rose-600" : "text-slate-600"}`}
                      >
                        {formatMoney(openingBalance)}
                      </td>
                    </tr>
                    {ledgerData.map((item: any, idx: number) => (
                      <tr
                        key={idx}
                        className="hover:bg-slate-50 transition-colors"
                      >
                        <td className="px-4 py-3 text-center">
                          <Checkbox
                            checked={selectedIds.includes(item.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedIds([...selectedIds, item.id]);
                              } else {
                                setSelectedIds(selectedIds.filter((id) => id !== item.id));
                              }
                            }}
                          />
                        </td>
                        <td className="px-4 py-3 text-center text-slate-600">
                          {formatDate(item.date)}
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-900">
                          {resolveEntityType(item.refId) ? (
                            <EntityLink
                              entityType={resolveEntityType(item.refId)!}
                              code={item.refId}
                            />
                          ) : (
                            item.refId || "-"
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {item.description}
                        </td>
                        <td className="px-4 py-3 text-right text-rose-600 font-medium">
                          {item.debit > 0 ? formatMoney(item.debit) : "-"}
                        </td>
                        <td className="px-4 py-3 text-right text-emerald-600 font-medium">
                          {item.credit > 0 ? formatMoney(item.credit) : "-"}
                        </td>
                        <td
                          className={`px-4 py-3 text-right font-bold ${item.balance > 0 ? "text-rose-600" : "text-emerald-600"}`}
                        >
                          {formatMoney(item.balance)}
                        </td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {isPrintOpen && (
        <PrintDialog
          open={isPrintOpen}
          onClose={() => setIsPrintOpen(false)}
          type="custom"
          title="In Biên Bản Đối Soát Công Nợ"
        >
          <VendorDebtReconciliationPrintTemplate
            data={{
              id: vendor?.code || vendorId,
              vendorName: vendor?.name,
              vendorCode: vendor?.code,
              vendorAddress: vendor?.address,
              contactPerson: vendor?.contactPerson,
              phone: vendor?.phone,
              asOfDate: new Date(),
              openingBalance: data?.openingBalance || 0,
              // sổ NCC: Phát sinh nợ (mua hàng / gia công) là cột nợ, thanh toán là cột có
              totalIncurred: ledgerData.reduce((s: number, i: any) => s + Number(i.debit || 0), 0),
              totalPaid: ledgerData.reduce((s: number, i: any) => s + Number(i.credit || 0), 0),
              closingBalance: data?.closingBalance || 0,
              transactions: ledgerData.filter((i: any) => selectedIds.includes(i.id)),
            }}
          />
        </PrintDialog>
      )}
    </StandardDetailLayout>
  );
}
